<template>
  <div class="projet-part">
    <h2 id="problematiques">{{ $t("projetPage.mon-annee.problematiques.titre") }}</h2>

    <InputWithValidation
      v-model="problematiquesIdentifiees"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
      type="textarea"
      :label="$t('projetPage.mon-annee.problematiques.problematiques')"
      :input-notice="$t('projetPage.mon-annee.problematiques.problematiques-message')"
      maxlength="1400"
      :disabled="readonly || !isPorteurProjet"
      vid="problematiques_problematiquesIdentifiees"
      @manual-change="changeProblematiques"
    />
    <div
      v-if="
        projetAnnuel.objectifAutre || (projetAnnuel.objectifs && projetAnnuel.objectifs.length > 0)
      "
    >
      <!-- Only for V1 migrated projects -->
      <GroupCheckboxesWithValidation
        v-model="checkedObjectifCheckBoxesStrings"
        :validation-activated="false"
        :show-validation-errors="false"
        label="Comment avez-vous affiné ou redéfini le ou les objectif·s et la ou les action·s de votre aire éducative pour cette nouvelle année ?"
        :data="objectifCheckBoxesStrings"
        :vertical-display="true"
        input-label="<b>Autre</b>"
        :ext-input-value="projetAnnuel.objectifAutre"
        vid="objectifs"
        :disabled="readonly || !isPorteurProjet"
      />
      <br />
    </div>

    <InputWithValidation
      v-model="procedesDeterminationActions"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT' ? 'required' : ''"
      type="textarea"
      :label="$t('projetPage.mon-annee.problematiques.actions')"
      :input-notice="$t('projetPage.mon-annee.problematiques.actions-message')"
      maxlength="1400"
      :disabled="readonly || !isPorteurProjet"
      vid="problematiques_procedesDeterminationActions"
      @manual-change="changeProblematiques"
    />
    <b-message
      v-if="
        validationActivated &&
        (projetAnnuel.typeDossier === 'DOSSIER_LABELLISATION' ||
          projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT') &&
        showRequiredMessage
      "
      type="is-danger"
    >
      {{ $t("projetPage.mon-annee.autres-infos.labellisation-renouvellement") }}
    </b-message>
    <InputWithValidation
      v-model="problematiquesLiens"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      type="textarea"
      :label="$t('projetPage.mon-annee.problematiques.liens')"
      :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : calculatedRules"
      maxlength="1400"
      :disabled="readonly || !isPorteurProjet"
      vid="problematiques_problematiquesLiens"
      @manual-change="changeProblematiques"
    />
    <InputWithValidation
      v-model="productionsEleves"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      type="textarea"
      :label="$t('projetPage.mon-annee.problematiques.productions')"
      maxlength="1400"
      :disabled="readonly || !isPorteurProjet"
      vid="problematiques_productionsEleves"
      @manual-change="changeProblematiques"
    />

    <div class="my-document-container">
      <FileWithValidation
        :label="$t(`projetPage.mon-annee.etat-des-lieux.fichier-titre`)"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :placeholder="$t(`projetPage.infoAdministratives.charte.televerser`)"
        :input-notice="$t('projetPage.mon-annee.problematiques.productions-message')"
        :file-operation-url="problematiquesFileUrl"
        :disabled="readonly"
        vid="problematiques_fichierProblematiques_prod"
        :model-value="projetAnnuel.problematiquesFilePresent"
        default-file-name="PROBLEMATIQUES_PRODUCTION.pdf"
        @add-file="addFile"
        @delete-file="deleteFile"
      />
      <FileWithValidation
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :placeholder="$t(`projetPage.infoAdministratives.charte.televerser`)"
        :input-notice="$t('projetPage.mon-annee.problematiques.transdis-message')"
        :file-operation-url="activitesTransDisFileUrl"
        :disabled="readonly"
        vid="problematiques_fichierProblematiques_transdis"
        :model-value="projetAnnuel.activitesTransDisrFilePresent"
        default-file-name="PROBLEMATIQUES_NOTIONS.pdf"
        @add-file="addTransdisFile"
        @delete-file="deleteTransdisFile"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import FileWithValidation from "@/views/inputs/FileWithValidation.vue"
import { FileService } from "@/services/rest/utils/FileService"
import GroupCheckboxesWithValidation from "@/views/inputs/GroupCheckBoxesWithValidation.vue"
import { BMessage } from "buefy"

const OBJECTIF_STRINGS = {
  ENRICHISSEMENT:
    "<b>Enrichissement de l'étude du site de l'année passée et comparaison</b><br/>redéfinition des objectifs de l'aire éducative en fontion des nouveaux éléments<br/>",
  NOUVELLE_ETUDE:
    "<b>Nouvelle étude du site sans utiliser la précédente</b><br/>nouveaux objectifs, nouvelles actions<br/>",
  REPRISE: "<b>Reprise des objectifs/actions de l'an passé</b><br/> sans nouvelle étude<br/>",
  AUTRE: "<b>Autre</b>",
}

export default defineComponent({
  name: "ProblematiquesOrientationsActionsView",
  components: {
    InputWithValidation,
    FileWithValidation,
    GroupCheckboxesWithValidation,
    BMessage,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["projet-annuel-updated", "dirtify-project", "trigger-validation"],
  data() {
    return {
      fileService: FileService.INSTANCE,
      problematiquesFileUrl: "",
      activitesTransDisFileUrl: "",
      calculatedRules: "required",
      showRequiredMessage: true,
      objectifCheckBoxesStrings: Object.values(OBJECTIF_STRINGS).filter(
        (value) => value !== OBJECTIF_STRINGS.AUTRE,
      ),
      checkedObjectifCheckBoxesStrings: [] as string[],
    }
  },
  computed: {
    // Computed properties pour éviter les mutations de props dans v-model
    problematiquesIdentifiees: {
      get(): string {
        return this.projetAnnuel.problematiquesIdentifiees || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.problematiquesIdentifiees = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
    procedesDeterminationActions: {
      get(): string {
        return this.projetAnnuel.procedesDeterminationActions || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.procedesDeterminationActions = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
    problematiquesLiens: {
      get(): string {
        return this.projetAnnuel.problematiquesLiens || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.problematiquesLiens = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
    productionsEleves: {
      get(): string {
        return this.projetAnnuel.productionsEleves || ""
      },
      set(value: string): void {
        const updatedProjet = { ...this.projetAnnuel }
        updatedProjet.productionsEleves = value
        this.$emit("projet-annuel-updated", updatedProjet)
      },
    },
  },
  watch: {
    projetAnnuel: {
      handler(): void {
        if (!this.projetAnnuel || !this.projetAnnuel.id) {
          return
        }
        this.problematiquesFileUrl = this.fileService.buildUniqueFileURL(
          this.projetAnnuel.id,
          "PROBLEMATIQUES",
        )
        this.activitesTransDisFileUrl = this.fileService.buildUniqueFileURL(
          this.projetAnnuel.id,
          "ACTIVITES_TRANSDISCIPLINAIRES",
        )
      },
      immediate: true,
    },
    "projetAnnuel.conseilsLiens": {
      handler(): void {
        this.updateProblematiquesLiensRules()
      },
    },
    "projetAnnuel.diffusionLiensEnseignements": {
      handler(): void {
        this.updateProblematiquesLiensRules()
      },
    },
    "projetAnnuel.etatDesLieuxLiensEnseignements": {
      handler(): void {
        this.updateProblematiquesLiensRules()
      },
    },
    "projetAnnuel.problematiquesLiens": {
      handler(): void {
        this.updateProblematiquesLiensRules()
      },
    },
  },
  created(): void {
    if (this.projetAnnuel.id) {
      this.problematiquesFileUrl = this.fileService.buildUniqueFileURL(
        this.projetAnnuel.id,
        "PROBLEMATIQUES",
      )
      this.activitesTransDisFileUrl = this.fileService.buildUniqueFileURL(
        this.projetAnnuel.id,
        "ACTIVITES_TRANSDISCIPLINAIRES",
      )
    }
    const objectifString: string[] = []
    if (this.projetAnnuel.objectifs) {
      if (this.projetAnnuel.objectifs.includes("ENRICHISSEMENT")) {
        objectifString.push(OBJECTIF_STRINGS.ENRICHISSEMENT)
      }
      if (this.projetAnnuel.objectifs.includes("NOUVELLE_ETUDE")) {
        objectifString.push(OBJECTIF_STRINGS.NOUVELLE_ETUDE)
      }
      if (this.projetAnnuel.objectifs.includes("REPRISE")) {
        objectifString.push(OBJECTIF_STRINGS.REPRISE)
      }
      if (this.projetAnnuel.objectifs.includes("AUTRE")) {
        objectifString.push(OBJECTIF_STRINGS.AUTRE)
      }
    }
    this.checkedObjectifCheckBoxesStrings = objectifString
  },
  mounted(): void {
    this.updateProblematiquesLiensRules()
  },
  methods: {
    updateProblematiquesLiensRules(): void {
      if (
        !this.projetAnnuel.conseilsLiens &&
        !this.projetAnnuel.diffusionLiensEnseignements &&
        !this.projetAnnuel.etatDesLieuxLiensEnseignements
      ) {
        this.calculatedRules = "required"
      } else {
        if (this.projetAnnuel.problematiquesLiens) {
          this.calculatedRules = "required"
        } else {
          this.calculatedRules = ""
        }
      }

      this.showRequiredMessage = !(
        this.projetAnnuel.problematiquesLiens ||
        this.projetAnnuel.conseilsLiens ||
        this.projetAnnuel.diffusionLiensEnseignements ||
        this.projetAnnuel.etatDesLieuxLiensEnseignements
      )
    },

    changeProblematiques(): void {
      this.updateProblematiquesLiensRules()
      this.$emit("dirtify-project")
    },

    addFile(): void {
      this.updateProjetAnnuelFile("problematiquesFilePresent", true)
    },

    deleteFile(): void {
      this.updateProjetAnnuelFile("problematiquesFilePresent", false)
    },

    addTransdisFile(): void {
      this.updateProjetAnnuelFile("activitesTransDisrFilePresent", true)
    },

    deleteTransdisFile(): void {
      this.updateProjetAnnuelFile("activitesTransDisrFilePresent", false)
    },

    updateProjetAnnuelFile<K extends keyof ProjetAnnuelDTO>(
      attributeName: K,
      isPresent: ProjetAnnuelDTO[K],
    ): void {
      const updatedProjet = { ...this.projetAnnuel }
      updatedProjet[attributeName] = isPresent
      this.$emit("projet-annuel-updated", updatedProjet)
      this.$emit("trigger-validation")
    },
  },
})
</script>

<style lang="scss" scoped></style>
